import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Loader2, Users, UserPlus, LogIn } from 'lucide-react';

interface InviteDetails {
  teamName: string;
  role: string;
  inviterEmail: string;
  email: string;
  expiresAt: string;
  alreadyAccepted?: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

export default function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, _hasHydrated } = useAuthStore();

  const token = searchParams.get('token');

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);

  // Fetch invite details (public endpoint — works without auth)
  useEffect(() => {
    if (!_hasHydrated) return;
    if (!token) {
      setError('No invite token provided.');
      setLoading(false);
      return;
    }
    fetchInviteDetails();
  }, [token, _hasHydrated]);

  async function fetchInviteDetails() {
    try {
      // Public endpoint — no auth required
      const res = await api.get(`/auth/invite/${token}`);
      setInvite(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message ?? 'Invalid or expired invitation.');
    } finally {
      setLoading(false);
    }
  }

  async function handleAccept() {
    if (!token) return;

    setAccepting(true);
    try {
      await api.post(`/teams/invitations/${token}/accept`);
      setAccepted(true);
      toast.success('Invitation accepted! You have joined the team.');
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message ?? 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  }

  function handleSignIn() {
    // Preserve invite URL so login redirects back here
    navigate('/login', {
      state: { from: { pathname: '/invite', search: `?token=${token}` } },
    });
  }

  function handleGoogleSignIn() {
    // Save redirect so OAuth callback returns to the invite page
    sessionStorage.setItem('auth_redirect', `/invite?token=${token}`);
    window.location.href = `${API_URL}/auth/google`;
  }

  function handleCreateAccount() {
    navigate('/register', {
      state: { from: { pathname: '/invite', search: `?token=${token}` } },
    });
  }

  // ── Loading ──────────────────────────────────────────────────────────
  if (!_hasHydrated || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  // ── Already Accepted (auto-accepted during signup) ────────────────────
  if (invite?.alreadyAccepted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4 dark:bg-gray-950">
        <div className="w-full max-w-md rounded-xl border border-green-200 bg-white p-8 text-center shadow-sm dark:border-green-800 dark:bg-gray-900">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="mb-2 text-xl font-bold text-green-800 dark:text-green-300">
            You&apos;re already on the team!
          </h1>
          <p className="mb-6 text-sm text-green-600 dark:text-green-400">
            You joined <strong>{invite.teamName}</strong> as{' '}
            <span className="capitalize">{invite.role}</span>.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4 dark:bg-gray-950">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-8 text-center shadow-sm dark:border-red-800 dark:bg-gray-900">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <Users className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="mb-2 text-xl font-bold text-red-800 dark:text-red-300">
            Invalid Invitation
          </h1>
          <p className="mb-6 text-sm text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Accepted ─────────────────────────────────────────────────────────
  if (accepted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4 dark:bg-gray-950">
        <div className="w-full max-w-md rounded-xl border border-green-200 bg-white p-8 text-center shadow-sm dark:border-green-800 dark:bg-gray-900">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="mb-2 text-xl font-bold text-green-800 dark:text-green-300">
            Welcome to the team!
          </h1>
          <p className="mb-6 text-sm text-green-600 dark:text-green-400">
            You have joined <strong>{invite?.teamName}</strong> as{' '}
            <span className="capitalize">{invite?.role}</span>.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Invite Details ───────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4 dark:bg-gray-950">
      <div className="w-full max-w-md rounded-xl border bg-white p-8 shadow-sm dark:bg-gray-900">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
            <UserPlus className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold">Team Invitation</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            You&apos;ve been invited to join a team
          </p>
        </div>

        {invite && (
          <div className="space-y-5">
            {/* Invite details card */}
            <div className="rounded-lg border bg-gray-50 p-4 dark:bg-gray-800">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Team</span>
                  <span className="font-semibold">{invite.teamName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Your role</span>
                  <span className="rounded-full bg-blue-100 px-3 py-0.5 text-sm font-medium capitalize text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    {invite.role}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Invited by</span>
                  <span className="text-sm font-medium">{invite.inviterEmail}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Expires</span>
                  <span className="text-sm">{new Date(invite.expiresAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* ── Authenticated: show accept button ────────────────────── */}
            {isAuthenticated ? (
              <button
                onClick={handleAccept}
                disabled={accepting}
                className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {accepting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  'Accept Invitation'
                )}
              </button>
            ) : (
              /* ── Not authenticated: show sign-in / sign-up options ──── */
              <div className="space-y-3">
                <p className="text-muted-foreground text-center text-sm">
                  Sign in or create an account to accept this invitation
                </p>

                {/* Google OAuth */}
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className="hover:bg-muted flex w-full items-center justify-center gap-3 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Continue with Google
                </button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-400 dark:bg-gray-900">or</span>
                  </div>
                </div>

                {/* Email sign in */}
                <button
                  onClick={handleSignIn}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <LogIn className="h-4 w-4" />
                  Sign in with email
                </button>

                {/* Create account link */}
                <p className="text-center text-sm">
                  <span className="text-muted-foreground">Don&apos;t have an account? </span>
                  <button
                    onClick={handleCreateAccount}
                    className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    Create one
                  </button>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
